// COP ↔ hub auth-API. COP-en serveres fra samme opphav som hubben (Caddy ruter
// /auth, /admin, /ws til hubben), så relative kall + cookie-sesjon fungerer.
// Sesjonen ligger i en HttpOnly-cookie — JS ser den aldri (XSS kan ikke stjele
// den); nettleseren sender den automatisk, også på WS-handshaket.
import type { User } from '@skade/rbac';

async function post(path: string, body: unknown): Promise<{ status: number; data: any }> {
  const r = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  let data: any = null;
  try { data = await r.json(); } catch { /* tomt svar */ }
  return { status: r.status, data };
}

/** Hvem er jeg? null hvis ikke innlogget. */
export async function fetchMe(): Promise<User | null> {
  try {
    const r = await fetch('/auth/me', { credentials: 'same-origin' });
    if (r.status !== 200) return null;
    return (await r.json()).user as User;
  } catch {
    return null;
  }
}

export type AuthResult = { ok: true; user: User } | { ok: false; error: string };

export async function login(userId: string, passphrase: string): Promise<AuthResult> {
  const { status, data } = await post('/auth/login', { userId, passphrase });
  if (status === 200 && data?.user) return { ok: true, user: data.user };
  return { ok: false, error: data?.error || `innlogging feilet (${status})` };
}

/**
 * TRINN 1 av registrering: ber hubben sende en fersk bekreftelseskode som
 * GNÅ-melding til `claimedSubject` — den GNÅ-identiteten brukeren PÅSTÅR å
 * være. Koden selv kommer ALDRI i dette svaret (den finnes bare i
 * GNÅ-meldingen); herfra vet vi bare om forespørselen ble godtatt.
 */
export async function enrollStart(code: string, claimedSubject: string): Promise<{ ok: true } | { ok: false; error: string }> {
  const { status, data } = await post('/auth/enroll/start', { code, claimedSubject });
  if (status === 200 && data?.ok) return { ok: true };
  return { ok: false, error: data?.error || `forespørsel avvist (${status})` };
}

/**
 * TRINN 2: bekreftelseskoden brukeren nettopp fikk over GNÅ + nytt passord.
 * Bruker-id-en her VELGES IKKE av klienten — hubben utleder den av adressaten
 * koden ble sendt til (§4 anti-spoof).
 */
export async function enrollComplete(code: string, nonce: string, passphrase: string): Promise<AuthResult> {
  const { status, data } = await post('/auth/enroll/complete', { code, nonce, passphrase });
  if (status === 200 && data?.user) return { ok: true, user: data.user };
  return { ok: false, error: data?.error || `registrering feilet (${status})` };
}

export async function logout(): Promise<void> {
  try { await post('/auth/logout', {}); } catch { /* uansett */ }
}

/**
 * QA-018 — løs inn et passord-reset-token: nytt passord på DEN EKSISTERENDE
 * bruker-id-en koden ble utstedt til. Ingen sesjon kreves (tokenet ER
 * autorisasjonen); ved gyldig innløsning logges brukeren rett inn, akkurat
 * som ved `enrollComplete`.
 */
export async function resetPassword(code: string, userId: string, passphrase: string): Promise<AuthResult> {
  const { status, data } = await post('/auth/reset-passord', { code, userId, passphrase });
  if (status === 200 && data?.user) return { ok: true, user: data.user };
  return { ok: false, error: data?.error || `reset feilet (${status})` };
}

/** Admin: utsted et enrolment-token (krever lagfører+ og org innen egen scope). */
export async function issueEnrolToken(params: {
  role: string; org: Record<string, string>; ttlMs?: number; maxUses?: number;
}): Promise<{ ok: true; code: string; expiresAt: number } | { ok: false; error: string }> {
  const { status, data } = await post('/admin/enrol-token', params);
  if (status === 200 && data?.code) return { ok: true, code: data.code, expiresAt: data.expiresAt };
  return { ok: false, error: data?.error || `utstedelse feilet (${status})` };
}
